import * as Api from '@ViewModels';
import { InfiniteData } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../models/NameUtils';
import {
	invalidateGetPhoneNumbers,
	useAssignUsersToPhoneNumberMutation,
	useChangePhoneNumberOwnerMutation,
	useDeactivateTextingPhoneNumber,
	useRemoveAssignedUsersFromPhoneNumberMutation,
	useUpdatePhoneNumberScopeMutation,
} from '../../../../../queries';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { Dropdown } from '../../../../../web/components/Dropdown';
import { Modal } from '../../../../../web/components/Modal';
import { WarningIconSmall, WarningIconType } from '../../../../../web/components/svgs/icons/WarningIcon';
import { IAutoCompleteSearchFieldComponent } from '../../../../../web/components/autocomplete/AutoCompleteSearchField';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../../web/components/autocomplete/SimpleAutoCompleteSearchField';
import { DangerIcon } from '../../../../../web/components/svgs/icons/DangerIcon';
import { MoreIcon } from '../../../../../web/components/svgs/icons/MoreIcon';
import { SharedNumberIcon } from '../../../../../web/components/svgs/icons/SharedNumberIcon';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { PlusIcon } from '../../../../../web/components/svgs/icons/PlusIcon';
import { brandPrimary, charmGray, destructive } from '../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { styleSheet } from './styles';

const deactivatePhoneNumberConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		title: 'Yes, deactivate this number',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const convertToSharedConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		title: 'Yes, mark as shared',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

const unshareNumberConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		title: 'Yes, unshare this number',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

export const ManageUsersModal = ({
	phoneNumber,
	allPhoneNumbers,
	onSave,
	onCancel,
	impersonationContext,
}: {
	phoneNumber: Api.ITelephonyConfiguration;
	allPhoneNumbers: InfiniteData<Api.IPagedCollection<Api.ITelephonyConfiguration>>;
	onSave: () => void;
	onCancel: () => void;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const [mode, setMode] = React.useState<'normal' | 'change-owner'>('normal');
	const [pendingAdditions, setPendingAdditions] = React.useState<Api.IUser[]>([]);
	const [pendingRemovals, setPendingRemovals] = React.useState<string[]>([]);
	const [pendingNewOwnerId, setPendingNewOwnerId] = React.useState<string | null>(null);
	const [isSaving, setIsSaving] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const searchFieldInnerRef = React.useRef<IAutoCompleteSearchFieldComponent>(null);

	const isShared = phoneNumber.phoneNumberScope === Api.PhoneNumberScope.Account;
	const currentOwnerId = phoneNumber.creator?.id;

	// Build set of user IDs who already have phone numbers assigned
	const usersWithPhoneNumbers = React.useMemo(() => {
		const userIds = new Set<string>();
		allPhoneNumbers?.pages?.forEach(page => {
			page.values.forEach(config => {
				config.assignedUsers.forEach(user => {
					userIds.add(user.id);
				});
			});
		});
		return userIds;
	}, [allPhoneNumbers]);

	// API Mutations
	const assignUsersMutation = useAssignUsersToPhoneNumberMutation({
		impersonationContext,
		onSuccess: () => {
			setErrorMessage(null);
			// Success handled in handleSave
		},
		onError: error => {
			setErrorMessage(`Failed to assign users: ${error.systemMessage || 'Unknown error'}`);
			setIsSaving(false);
		},
	});

	const removeUsersMutation = useRemoveAssignedUsersFromPhoneNumberMutation({
		impersonationContext,
		onSuccess: () => {
			setErrorMessage(null);
			// Success handled in handleSave
		},
		onError: error => {
			setErrorMessage(`Failed to remove users: ${error.systemMessage || 'Unknown error'}`);
			setIsSaving(false);
		},
	});

	const changeOwnerMutation = useChangePhoneNumberOwnerMutation({
		impersonationContext,
		onSuccess: () => {
			setErrorMessage(null);
			// Success handled in handleSave
		},
		onError: error => {
			setErrorMessage(`Failed to change owner: ${error.systemMessage || 'Unknown error'}`);
			setIsSaving(false);
		},
	});

	// Compute displayed users (current users + additions, removals shown with strikethrough)
	const displayedUsers = React.useMemo(() => {
		return [...phoneNumber.assignedUsers, ...pendingAdditions];
	}, [phoneNumber.assignedUsers, pendingAdditions]);

	const hasPendingChanges = pendingAdditions.length > 0 || pendingRemovals.length > 0;

	const handleRemoveUser = (userId: string) => {
		if (pendingRemovals.includes(userId)) {
			// Undo: remove from pending removals
			setPendingRemovals(pendingRemovals.filter(id => id !== userId));
		} else {
			// Mark for removal
			setPendingRemovals([...pendingRemovals, userId]);
		}
	};

	const onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		suggestedViewModel.addParam({ includeDeactivated: false });
		return suggestedViewModel.impersonate(impersonationContext);
	};

	const onItemSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<string | Api.IEntity | Api.IUser>) => {
		const user = e.selection as Api.IUser;
		// Don't add if already in the list
		if (!pendingAdditions.find(u => u.id === user.id) && !phoneNumber.assignedUsers.find(u => u.id === user.id)) {
			setPendingAdditions([...pendingAdditions, user]);
			searchFieldInnerRef.current?.clearInput();
		}
	};

	const onSearchFieldRef = (ref?: IAutoCompleteSearchFieldComponent) => {
		if (ref) {
			searchFieldInnerRef.current = ref;
		}
	};

	const handleRemoveAddedUser = (user: Api.IUser) => {
		const newUsersToAdd = pendingAdditions.filter(u => u.id !== user.id);
		setPendingAdditions(newUsersToAdd);
		setErrorMessage(null); // Clear error when user removes conflicting user
	};

	const handleOwnerSelection = (userId: string) => {
		setPendingNewOwnerId(userId);
	};

	const toggleMode = () => {
		if (mode === 'normal') {
			setMode('change-owner');
			setPendingNewOwnerId(currentOwnerId); // Default to current owner
		} else {
			setMode('normal');
			setPendingNewOwnerId(null);
		}
	};

	const isChangeOwnerMode = mode === 'change-owner';

	const handleCancel = () => {
		setErrorMessage(null);
		onCancel();
	};

	// Save handler with validation and API orchestration
	const handleSave = async () => {
		// Validation: Cannot remove all users
		const finalUserCount = phoneNumber.assignedUsers.length - pendingRemovals.length + pendingAdditions.length;
		if (finalUserCount === 0) {
			toast.error('Cannot remove all users from the phone number');
			return;
		}

		// Validation: If in change owner mode, must have selected an owner
		if (isChangeOwnerMode && !pendingNewOwnerId) {
			toast.error('Please select a new owner');
			return;
		}

		// Validation: Cannot change owner to a pending addition (they must be assigned first)
		if (isChangeOwnerMode && pendingNewOwnerId && pendingAdditions.find(u => u.id === pendingNewOwnerId)) {
			toast.error(
				'Cannot set owner to a user who is not yet assigned. Please save the assignment first, then change ownership.'
			);
			return;
		}

		// Validation: Check if any pending additions already have a phone number
		const conflictingUsers = pendingAdditions.filter(u => usersWithPhoneNumbers.has(u.id));
		if (conflictingUsers.length > 0) {
			const userNames = conflictingUsers.map(u => getDisplayName(u)).join(', ');
			setErrorMessage(`The following users already have a phone number assigned and cannot be added: ${userNames}`);
			return;
		}

		setIsSaving(true);

		try {
			// Execute API calls in sequence
			if (pendingAdditions.length > 0) {
				await assignUsersMutation.mutateAsync({
					phoneNumber,
					userIds: pendingAdditions.map(u => u.id),
				});
			}

			if (pendingRemovals.length > 0) {
				await removeUsersMutation.mutateAsync({
					phoneNumber,
					userIds: pendingRemovals,
				});
			}

			if (isChangeOwnerMode && pendingNewOwnerId && pendingNewOwnerId !== currentOwnerId) {
				await changeOwnerMutation.mutateAsync({
					phoneNumber,
					newOwnerId: pendingNewOwnerId,
				});
			}

			toast.success('Successfully updated phone number users');
			setIsSaving(false);
			onSave(); // Call parent callback to close modal and refresh data
		} catch (error) {
			// Error already handled by mutation onError callbacks
			console.error('Error updating phone number users:', error);
			setIsSaving(false);
		}
	};

	return (
		<Modal isOpen>
			<article className={css(styleSheet.modalContainer)}>
				<div>
					<h3>Connected User(s) for {phoneNumber.number}</h3>
					<figure className={css(styleSheet.sharedNumberModalHeader)}>
						<SharedNumberIcon />
						<figcaption>Shared Number</figcaption>
					</figure>
				</div>

				{/* Error Banner */}
				{errorMessage && (
					<div className={css(styleSheet.errorBanner)}>
						<div className={css(styleSheet.errorWarning)}>
							<DangerIcon />
							<p className={css(styleSheet.errorText)}>{errorMessage}</p>
						</div>
					</div>
				)}

				{/* User List */}
				<ul className={css(styleSheet.unorderedList)}>
					{displayedUsers.map((user, index) => {
						const isOwner = user.id === currentOwnerId;
						const isPendingAddition = pendingAdditions.find(u => u.id === user.id);
						const isPendingRemoval = pendingRemovals.includes(user.id);
						const hasPhoneNumber = usersWithPhoneNumbers.has(user.id);
						const hasConflict = isPendingAddition && hasPhoneNumber;

						return (
							<li
								key={`manage-number-${user.id}`}
								className={css(
									styleSheet.lineItem,
									index % 2 === 1 && styleSheet.lineItemAlternate,
									isPendingRemoval && styleSheet.lineItemPendingRemoval,
									isPendingAddition && styleSheet.lineItemPendingAddition
								)}
							>
								{/* Radio button for change owner mode */}
								{isChangeOwnerMode ? (
									<input
										type='radio'
										checked={pendingNewOwnerId === user.id}
										onChange={() => handleOwnerSelection(user.id)}
										disabled={isPendingAddition !== undefined}
									/>
								) : null}

								{/* User name with owner label */}
								<span className={css(isPendingRemoval && styleSheet.userNameStrikethrough)}>
									{getUsersDisplayName(user, phoneNumber, isShared)}
								</span>

								{/* Warning icon for users who already have a phone number */}
								{hasConflict ? (
									<span className={css(styleSheet.userWarning)} title='This user already has a phone number assigned'>
										<WarningIconSmall type={WarningIconType.Red} />
									</span>
								) : null}

								{/* Action buttons on the right */}
								{!isChangeOwnerMode && !isOwner && !isPendingAddition ? (
									<button
										onClick={() => handleRemoveUser(user.id)}
										className={css(styleSheet.trashButton)}
										title={`Remove ${getDisplayName(user)}`}
									>
										<TrashIcon fillColor={isPendingRemoval ? charmGray : destructive} />
									</button>
								) : null}
								{!isChangeOwnerMode && isPendingAddition ? (
									<button
										onClick={() => handleRemoveAddedUser(user)}
										className={css(styleSheet.trashButton)}
										title={`Remove ${getDisplayName(user)}`}
									>
										<TrashIcon fillColor={charmGray} />
									</button>
								) : null}
								{!isChangeOwnerMode && isOwner ? (
									<button
										onClick={toggleMode}
										disabled={hasPendingChanges}
										title={hasPendingChanges ? 'Save or cancel pending changes before changing owner' : undefined}
										className={css(
											styleSheet.trashButton,
											baseStyleSheet.ctaButtonReverse,
											hasPendingChanges && styleSheet.disabledButton
										)}
									>
										Change
									</button>
								) : null}
								{isChangeOwnerMode && isOwner ? (
									<button onClick={toggleMode} className={css(styleSheet.trashButton, baseStyleSheet.ctaButtonReverse)}>
										Cancel
									</button>
								) : null}
							</li>
						);
					})}
				</ul>

				{/* Add User Search (only in normal mode) */}
				{!isChangeOwnerMode ? (
					<div>
						<div className={css(styleSheet.addUserContainer)}>
							<div className={css(styleSheet.addUserSearchField)}>
								<SimpleAutoCompleteSearchField
									onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
									onItemSelected={onItemSelected}
									resultsLimit={5}
									pageSize={5}
									placeholder='Search Users to Add'
									type={Api.ResourceAutoCompleteViewModelType.User}
									onSearchFieldRef={onSearchFieldRef}
								/>
							</div>
							<div className={css(styleSheet.addButton)}>
								<PlusIcon fillColor={brandPrimary} width={16} height={16} />
							</div>
						</div>
					</div>
				) : null}

				{/* Footer */}
				<div className={css(styleSheet.modalActions)}>
					<button onClick={handleSave} disabled={isSaving} className={css(baseStyleSheet.ctaButton)}>
						{isSaving ? 'Saving...' : 'Save Changes'}
					</button>
					<button onClick={handleCancel} disabled={isSaving} className={css(baseStyleSheet.ctaButtonReverse)}>
						Cancel
					</button>
				</div>
			</article>
		</Modal>
	);
};

const getUsersDisplayName = (user: Api.IUser, phoneNumber: Api.ITelephonyConfiguration, isShared?: boolean) => {
	let value = getDisplayName(user);
	if (user.id === phoneNumber.creator?.id && isShared) {
		value = `${value} (owner)`;
	}
	return value;
};

export const TextingRow = ({
	phoneNumber,
	impersonationContext,
	setNumberToManage,
}: {
	phoneNumber: Api.ITelephonyConfiguration;
	impersonationContext: Api.IImpersonationContext;
	setNumberToManage: React.Dispatch<React.SetStateAction<Api.ITelephonyConfiguration>>;
}) => {
	const [showActionMenu, setShowActionMenu] = React.useState(false);
	const [showDeactivateConfirm, setShowDeactivateConfirm] = React.useState(false);
	const [showConvertToSharedConfirm, setShowConvertToSharedConfirm] = React.useState(false);
	const [showUnshareConfirm, setShowUnshareConfirm] = React.useState(false);
	const isShared = phoneNumber.phoneNumberScope === Api.PhoneNumberScope.Account;
	const isUserScoped = phoneNumber.phoneNumberScope === Api.PhoneNumberScope.User;

	const updatePhoneNumberScopeMutation = useUpdatePhoneNumberScopeMutation({
		impersonationContext,
		onSuccess: () => {
			setShowActionMenu(false);
			invalidateGetPhoneNumbers(impersonationContext);
		},
	});

	const deactivateNumberMutation = useDeactivateTextingPhoneNumber({
		impersonationContext,
		onSuccess: () => {
			toast.success('Phone number deactivated successfully');
			setShowDeactivateConfirm(false);
			setShowActionMenu(false);
			invalidateGetPhoneNumbers(impersonationContext);
		},
		onError: error => {
			toast.error(`Failed to deactivate: ${error.systemMessage || 'Unknown error'}`);
			setShowDeactivateConfirm(false);
		},
	});

	const onUpdateScopeClicked = () => {
		setShowConvertToSharedConfirm(true);
		setShowActionMenu(false);
	};

	const handleConvertToSharedConfirm = (_: void, cancel: boolean) => {
		if (cancel) {
			setShowConvertToSharedConfirm(false);
			return;
		}

		updatePhoneNumberScopeMutation.mutate({
			phoneNumber,
			scope: Api.PhoneNumberScope.Account,
		});
		setShowConvertToSharedConfirm(false);
	};

	const onUnshareClicked = () => {
		setShowUnshareConfirm(true);
		setShowActionMenu(false);
	};

	const handleUnshareConfirm = (_: void, cancel: boolean) => {
		if (cancel) {
			setShowUnshareConfirm(false);
			return;
		}

		updatePhoneNumberScopeMutation.mutate({
			phoneNumber,
			scope: Api.PhoneNumberScope.User,
		});
		setShowUnshareConfirm(false);
	};

	const onManageUsersClicked = () => {
		setNumberToManage(phoneNumber);
		setShowActionMenu(false);
	};

	const onDeactivateClicked = () => {
		setShowDeactivateConfirm(true);
		setShowActionMenu(false);
	};

	const handleDeactivateConfirm = (_: void, cancel: boolean) => {
		if (cancel) {
			setShowDeactivateConfirm(false);
			return;
		}

		deactivateNumberMutation.mutate({ id: phoneNumber.id });
	};

	const toggleActionMenu = () => {
		setShowActionMenu(!showActionMenu);
	};

	const canShare = !isShared;
	const canUnshare = isShared && phoneNumber.assignedUsers.length === 1;

	return (
		<>
			<tr className={css(styleSheet.tableRow)}>
				<td className={css(styleSheet.tableCell)}>
					<Dropdown
						anchor={
							<span className={css(styleSheet.actionsButton)}>
								<MoreIcon />
							</span>
						}
						className={css(styleSheet.actionsDropdown)}
						contentClassName={css(styleSheet.menu)}
						contentPositionY='bottom'
						isOpen={!!showActionMenu}
						onOpenChanged={toggleActionMenu}
						openOnClick={true}
					>
						<>
							{canShare ? (
								<div className={css(styleSheet.menuItem)} onClick={onUpdateScopeClicked}>
									Mark as a shared number
								</div>
							) : null}

							{canUnshare ? (
								<div className={css(styleSheet.menuItem)} onClick={onUnshareClicked}>
									Unshare this number
								</div>
							) : null}

							{isShared ? (
								<div className={css(styleSheet.menuItem)} onClick={onManageUsersClicked}>
									Manage Connected User(s)
								</div>
							) : null}

							{phoneNumber.assignedUsers.length === 1 ? (
								<ConditionalAccess permission={AdminPermission.SupportLevel3} or={AdminPermission.OnlyForDevelopers}>
									<div className={css(styleSheet.menuItem)} onClick={onDeactivateClicked}>
										Deactivate
									</div>
								</ConditionalAccess>
							) : null}

							{!canShare && !isShared && !isUserScoped ? (
								<div className={css(styleSheet.menuItem)}>
									<i>No actions available</i>
								</div>
							) : null}
						</>
					</Dropdown>
				</td>

				<td className={css(styleSheet.tableCell, styleSheet.phoneNumberCell)}>
					<span className={css(styleSheet.phoneNumber)}>{phoneNumber.number}</span>
					{isShared ? (
						<figure className={css(styleSheet.sharedNumber)}>
							<SharedNumberIcon />
							<figcaption>Shared Number</figcaption>
						</figure>
					) : null}
				</td>

				<td className={css(styleSheet.connectedUsersCell)}>
					{phoneNumber.assignedUsers.map(user => getUsersDisplayName(user, phoneNumber, isShared)).join(', ')}
				</td>

				<td className={css(styleSheet.tableCell)}>{phoneNumber.provider}</td>

				<td className={css(styleSheet.tableCell)}>{phoneNumber?.phoneNumber?.campaignId}</td>
			</tr>
			<ConfirmationDialog
				icon={<WarningIconSmall type={WarningIconType.Red} />}
				title='Deactivate Number?'
				options={deactivatePhoneNumberConfirmationOptions}
				modalProps={{
					isOpen: showDeactivateConfirm,
					onRequestClose: handleDeactivateConfirm,
				}}
			>
				<div className={css(styleSheet.deactivateDialogContent)}>
					<p className={css(styleSheet.deactivateDialogText)}>
						Are you sure you want to deactivate this number? This action cannot be undone.
					</p>
					<p className={css(styleSheet.deactivateDialogInfo)}>
						<strong>Phone Number:</strong> {phoneNumber.number}
					</p>
					<p className={css(styleSheet.deactivateDialogInfo)}>
						<strong>Assigned User:</strong> {phoneNumber.assignedUsers.map(user => getDisplayName(user)).join(', ')}
					</p>
				</div>
			</ConfirmationDialog>
			<ConfirmationDialog
				icon={<WarningIconSmall type={WarningIconType.Blue} />}
				title='Convert to Shared Number?'
				options={convertToSharedConfirmationOptions}
				modalProps={{
					isOpen: showConvertToSharedConfirm,
					onRequestClose: handleConvertToSharedConfirm,
				}}
			>
				<div className={css(styleSheet.deactivateDialogContent)}>
					<p className={css(styleSheet.deactivateDialogText)}>Are you sure you want to mark this as a shared number?</p>
					<p className={css(styleSheet.deactivateDialogInfo)}>
						<strong>Phone Number:</strong> {phoneNumber.number}
					</p>
				</div>
			</ConfirmationDialog>
			<ConfirmationDialog
				icon={<WarningIconSmall type={WarningIconType.Blue} />}
				title='Unshare Number?'
				options={unshareNumberConfirmationOptions}
				modalProps={{
					isOpen: showUnshareConfirm,
					onRequestClose: handleUnshareConfirm,
				}}
			>
				<div className={css(styleSheet.deactivateDialogContent)}>
					<p className={css(styleSheet.deactivateDialogText)}>Are you sure you want to unshare this number?</p>
					<p className={css(styleSheet.deactivateDialogInfo)}>
						<strong>Phone Number:</strong> {phoneNumber.number}
					</p>
				</div>
			</ConfirmationDialog>
		</>
	);
};
